// ./src/components/features/Tasks/TasksCalendar/TasksCalendar.tsx
//
// Presentational calendar view for an event's tasks / appointments. Does NO data
// fetching — the parent passes in ALL loaded tasks and this filters internally to the
// dated ones (scheduledAt != null), grouping them by day-in-timezone.
//
// Two modes:
//   • "agenda" — every dated day, chronological, as a day header + item rows.
//   • "month"  — a Mantine DatePicker month grid (self-navigating) with a dot Indicator
//                on days that have items, plus an agenda panel for the selected day.
//
// Clicking an item row calls onEditTask(task) so the parent opens its edit drawer.
// Category name / icon / color are resolved from the shared CategoriesContext, and all
// label / color / location helpers are reused from taskHelpers (no duplicated logic).

import React, { useMemo, useState } from "react";
import { Paper, Stack, Group, Text, Box, Badge, Indicator, Skeleton, Center, Flex } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { Task, TASK_TYPE, TASK_STATUS } from "@/utils/api";
import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import { AppIcon, EmptyState } from "@/components/generic";
import {
  getTaskTypeLabel,
  getTaskStatusLabel,
  getTaskStatusColor,
  getResponsibleKindIcon,
  formatTaskLocation,
} from "../taskHelpers";

dayjs.extend(utc);
dayjs.extend(timezone);

// ============================================
// Props
// ============================================

export interface TasksCalendarProps {
  tasks: Task[];
  eventTimezone?: string | null;
  mode: "agenda" | "month";
  onEditTask: (task: Task) => void;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
}

// ============================================
// Timezone + grouping helpers
// ============================================

const DAY_KEY_FORMAT = "YYYY-MM-DD";

const resolveTz = (tz?: string | null): string => {
  if (tz) {
    return tz;
  }

  try {
    return dayjs.tz.guess();
  } catch {
    return "UTC";
  }
};

/** The task's stored-UTC schedule, moved into the render timezone. */
const taskInTz = (task: Task, tz: string) => dayjs.utc(task.scheduledAt as string).tz(tz);

/** "Today" / "Tomorrow" / "dddd, MMM D" for a day key, relative to now-in-tz. */
const dayHeaderLabel = (dayKey: string, tz: string): string => {
  const day = dayjs.tz(dayKey, tz);
  const today = dayjs().tz(tz).startOf("day");
  const diff = day.startOf("day").diff(today, "day");

  if (diff === 0) {
    return "Today";
  }

  if (diff === 1) {
    return "Tomorrow";
  }

  return day.format("dddd, MMM D");
};

// ============================================
// Category resolution
// ============================================

interface CategoryMeta {
  name: string | null;
  icon: string | null;
  color: string | null;
}

// ============================================
// Item row (shared by both modes)
// ============================================

interface ItemRowProps {
  task: Task;
  tz: string;
  meta: CategoryMeta | null;
  onEditTask: (task: Task) => void;
}

const ItemRow: React.FC<ItemRowProps> = ({ task, tz, meta, onEditTask }) => {
  const isAppointment = task.taskType === TASK_TYPE.APPOINTMENT;
  const isDone = task.taskStatus === TASK_STATUS.DONE;

  const time = taskInTz(task, tz).format("h:mm A");
  const location = formatTaskLocation(task);
  const isTask = task.taskType === TASK_TYPE.TASK;

  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      onClick={() => onEditTask(task)}
      style={{ cursor: "pointer", opacity: isDone ? 0.6 : 1, transition: "opacity 150ms ease" }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        {/* Fixed-width, right-aligned time so the badge/title column lines up across rows. */}
        <Text
          size="sm"
          fw={500}
          c="dimmed"
          ta="right"
          style={{ width: 72, flexShrink: 0, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", paddingTop: 1 }}
        >
          {time}
        </Text>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Badge size="xs" variant={isAppointment ? "filled" : "light"} color={isAppointment ? "blue" : "gray"}>
              {getTaskTypeLabel(task.taskType)}
            </Badge>
            <Text size="md" fw={600} lineClamp={1}>
              {task.title || "—"}
            </Text>
          </Group>
          <Group gap="md" wrap="wrap" mt={4}>
            {meta?.name ? (
              <Group gap={4} wrap="nowrap">
                {meta.icon ? <AppIcon iconName={meta.icon} size={13} color={meta.color || undefined} /> : null}
                <Text size="md" c={meta.color || "dimmed"} lineClamp={1}>
                  {meta.name}
                </Text>
              </Group>
            ) : null}
            {task.responsibleLabel ? (
              <Group gap={4} wrap="nowrap" c="dimmed">
                <AppIcon iconName={getResponsibleKindIcon(task.responsibleKind)} size={13} />
                <Text size="md" lineClamp={1}>
                  {task.responsibleLabel}
                </Text>
              </Group>
            ) : null}
            {location ? (
              <Group gap={4} wrap="nowrap" c="dimmed">
                <AppIcon iconName="MapPin" size={13} />
                <Text size="md" lineClamp={1}>
                  {location}
                </Text>
              </Group>
            ) : null}
            {isTask ? (
              <Badge size="xs" variant="light" color={getTaskStatusColor(task.taskStatus)}>
                {getTaskStatusLabel(task.taskStatus)}
              </Badge>
            ) : null}
          </Group>
        </Box>
      </Group>
    </Paper>
  );
};

// ============================================
// Day group (header + rows)
// ============================================

interface DayGroupProps {
  dayKey: string;
  tz: string;
  items: Task[];
  metaOf: (task: Task) => CategoryMeta | null;
  onEditTask: (task: Task) => void;
}

const DayGroup: React.FC<DayGroupProps> = ({ dayKey, tz, items, metaOf, onEditTask }) => {
  return (
    <Stack gap="xs">
      <Group gap="xs" wrap="nowrap">
        <Text size="md" fw={600}>
          {dayHeaderLabel(dayKey, tz)}
        </Text>
        <Badge size="sm" variant="light" color="gray">
          {items.length}
        </Badge>
      </Group>
      <Stack gap="xs">
        {items.map((task) => (
          <ItemRow key={task.id} task={task} tz={tz} meta={metaOf(task)} onEditTask={onEditTask} />
        ))}
      </Stack>
    </Stack>
  );
};

// ============================================
// Main
// ============================================

export const TasksCalendar: React.FC<TasksCalendarProps> = ({
  tasks,
  eventTimezone,
  mode,
  onEditTask,
  isLoaded,
}) => {
  const tz = resolveTz(eventTimezone);
  const categoriesContext = useOptionalCategoriesContext();

  const metaOf = useMemo(() => {
    return (task: Task): CategoryMeta | null => {
      if (!task.categoryId) {
        return null;
      }

      const category = categoriesContext?.getById(task.categoryId);

      if (!category) {
        return null;
      }

      return { name: category.name || category.code || null, icon: category.icon ?? null, color: category.color ?? null };
    };
  }, [categoriesContext]);

  // Dated tasks, ascending, grouped by day-in-tz.
  const { dayKeys, groups } = useMemo(() => {
    const dated = tasks
      .filter((task) => !!task.scheduledAt)
      .sort((a, b) => dayjs.utc(a.scheduledAt as string).valueOf() - dayjs.utc(b.scheduledAt as string).valueOf());

    const map = new Map<string, Task[]>();

    dated.forEach((task) => {
      const key = taskInTz(task, tz).format(DAY_KEY_FORMAT);
      const bucket = map.get(key);

      if (bucket) {
        bucket.push(task);
      } else {
        map.set(key, [task]);
      }
    });

    return { dayKeys: Array.from(map.keys()), groups: map };
  }, [tasks, tz]);

  // Default selected day for month mode: today-in-tz if it has items, else the earliest dated day.
  const todayKey = dayjs().tz(tz).format(DAY_KEY_FORMAT);
  const initialSelectedDay = groups.has(todayKey) ? todayKey : dayKeys[0] ?? todayKey;
  const [selectedDay, setSelectedDay] = useState<string>(initialSelectedDay);

  // ── Loading ──
  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="md">
        {Array.from({ length: 4 }).map((_, i) => (
          <Stack gap="xs" key={i}>
            <Skeleton height={18} width={160} />
            <Skeleton height={64} radius="md" />
            <Skeleton height={64} radius="md" />
          </Stack>
        ))}
      </Stack>
    );
  }

  // ── Agenda ──
  if (mode === "agenda") {
    if (dayKeys.length === 0) {
      return (
        <EmptyState
          iconName="CalendarEvent"
          title="Nothing scheduled"
          description="Appointments and tasks with a due date show up here."
          size="md"
        />
      );
    }

    return (
      <Box style={{ overflowX: "auto" }}>
        <Stack gap="lg">
          {dayKeys.map((dayKey) => (
            <DayGroup
              key={dayKey}
              dayKey={dayKey}
              tz={tz}
              items={groups.get(dayKey) ?? []}
              metaOf={metaOf}
              onEditTask={onEditTask}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  // ── Month ──
  // Two-column on tablet+ (calendar left / selected-day list right); stacked on
  // phones (list under the calendar) via a responsive Flex direction.
  const selectedItems = groups.get(selectedDay) ?? [];

  return (
    <Flex direction={{ base: "column", md: "row" }} gap="xl" align="flex-start">
      {/* Calendar: fixed to its content width, centered on phones. */}
      <Box w={{ base: "100%", md: "auto" }} style={{ flexShrink: 0 }}>
        <Center>
          <DatePicker
            type="default"
            value={selectedDay}
            onChange={(value) => {
              if (value) {
                setSelectedDay(value);
              }
            }}
            size="md"
            renderDay={(dateString) => {
              const day = dayjs(dateString).format(DAY_KEY_FORMAT);
              const hasItems = groups.has(day);
              const dayOfMonth = dayjs(dateString).date();

              return (
                <Indicator size={6} color="blue" offset={-2} disabled={!hasItems}>
                  <div>{dayOfMonth}</div>
                </Indicator>
              );
            }}
          />
        </Center>
      </Box>

      {/* Selected-day list: fills the remaining width on desktop, full width on phones. */}
      <Box w={{ base: "100%", md: "auto" }} style={{ flex: 1, minWidth: 0, overflowX: "auto" }}>
        {selectedItems.length > 0 ? (
          <DayGroup
            dayKey={selectedDay}
            tz={tz}
            items={selectedItems}
            metaOf={metaOf}
            onEditTask={onEditTask}
          />
        ) : (
          <Text size="md" c="dimmed" ta={{ base: "center", md: "left" }}>
            Nothing scheduled on {dayHeaderLabel(selectedDay, tz)}.
          </Text>
        )}
      </Box>
    </Flex>
  );
};
